import { forwardRef, type ForwardRefRenderFunction, useMemo } from "react";
import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import { type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import {
  type PropertySummaryReuseFeedGridProps,
  type PropertySummaryReuseRow,
} from "@/features/propertySummary/types";
import { useGridPagination } from "../hooks/useGridPagination";

import { type ReusePicturesGetAPIResponse } from "@/features/propertySummary/api/ReusePicturesAPI";
import {
  RealEstateTypeLabels,
  RealEstateTypeValues,
} from "@/features/storedpictures/types/enums";
import { ReuseInformationRadioButtons } from "@/components/AcquireBook/ReuseInformationRadioButtons";
import type { ReuseUiData, ReuseUiDetailData } from "@/types/acquirebook";
import { type ReuseInfoGetAPIResponse } from "@/features/acquireBook/api/ReuseInfoAPI";

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

/**
 * _FeedGrid
 * reload関数を親コンポーネントに外部公開するために、関数名にはアンダースコアをつけている
 * @param _props
 * @param ref
 */
const _FeedGrid: ForwardRefRenderFunction<
  { reload: () => void },
  PropertySummaryReuseFeedGridProps
> = (_props, ref) => {
  let rows: PropertySummaryReuseRow[] = [];
  const { filterCondition, control } = _props;
  const { page, pageSize, changePage, changePageSize } = useGridPagination();
  const rowsPerPageOptions: number[] = [25, 50, 100];

  const genUiData = (data: ReuseInfoGetAPIResponse[]): ReuseUiData => {
    const reuseNew: ReuseUiDetailData = {
      acquiredDate: "",
      disabled: false,
      pictureRequestDetailId: 0,
    };

    const reuseUiData: ReuseUiData = {
      reuseNew,
      reuseOwn: null,
      reuseTrustart: null,
    };

    const own = data.find((item) => item.reuseOption === "REUSE_OWN");
    const trustart = data.find((item) => item.reuseOption === "REUSE_TRUSTART");

    if (own?.acquiredDateReuseOwn) {
      reuseUiData.reuseOwn = {
        acquiredDate: own.acquiredDateReuseOwn,
        disabled: false,
        pictureRequestDetailId: own.pictureRequestDetailId,
      };
    }

    if (trustart?.acquiredDateReuseTrustart) {
      reuseUiData.reuseTrustart = {
        acquiredDate: trustart.acquiredDateReuseTrustart,
        disabled: false,
        pictureRequestDetailId: trustart.pictureRequestDetailId,
      };
    }

    return reuseUiData;
  };

  const makeGridColDef = (): GridColDef[] => {
    return [
      {
        field: "type",
        headerName: "種別",
        width: 150,
        sortable: true,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          if (value === RealEstateTypeValues.BUILDING)
            return RealEstateTypeLabels.BUILDING;
          else if (value === RealEstateTypeValues.CONDOMINIUM)
            return RealEstateTypeLabels.CONDOMINIUM;
          else return RealEstateTypeLabels.LAND;
        },
      },
      {
        field: "prefecture",
        headerName: "都道府県",
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
      },
      {
        field: "location",
        headerName: "所在",
        width: 120,
        sortable: false,
        align: "left",
        headerAlign: "left",
      },
      {
        field: "chibanKaokuNumber",
        headerName: "地番または家屋番号",
        width: 180,
        sortable: false,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "reuse",
        headerName: "取得方法",
        width: 500,
        sortable: false,
        align: "left",
        headerAlign: "left",
        renderCell: ({
          id,
          row,
        }: GridRenderCellParams<string, PropertySummaryReuseRow>) => {
          if (row.reuseLand) {
            return (
              <ReuseInformationRadioButtons
                name={"pictureCheckOptionsLand"}
                control={control}
                reuseData={genUiData(row.reuseLand)}
              />
            );
          }
          if (row.reuseBuilding) {
            return (
              <ReuseInformationRadioButtons
                name={"pictureCheckOptionsBuilding"}
                control={control}
                reuseData={genUiData(row.reuseBuilding)}
              />
            );
          }
          return null;
        },
      },
    ];
  };

  const colDef = useMemo<GridColDef[]>(() => makeGridColDef(), []);

  const transferResponseToRows = (
    apiResponse: ReusePicturesGetAPIResponse
  ): PropertySummaryReuseRow[] => {
    const row: PropertySummaryReuseRow[] = [];
    row.push({
      id: 1,
      type: apiResponse.reuseLand.realEstateType,
      prefecture: apiResponse.reuseLand.prefName,
      chibanKaokuNumber: apiResponse.reuseLand.chibanKaokuNumber,
      location: apiResponse.reuseLand.locationFull,
      reuseLand: apiResponse.reuseLand.reusePictures,
    });

    row.push({
      id: 2,
      type: apiResponse.reuseBuilding.realEstateType,
      prefecture: apiResponse.reuseBuilding.prefName,
      chibanKaokuNumber: apiResponse.reuseBuilding.chibanKaokuNumber,
      location: apiResponse.reuseBuilding.locationFull,
      reuseBuilding: apiResponse.reuseBuilding.reusePictures,
    });

    return row;
  };

  if (!filterCondition) {
    return null;
  }

  rows = transferResponseToRows(filterCondition);

  const dataGridPaginationProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange: changePage,
    onPageSizeChange: changePageSize,
  };

  return (
    <StyledDataGrid
      autoHeight
      disableColumnMenu={true}
      disableSelectionOnClick={true}
      rows={rows}
      columns={colDef}
      hideFooter={true}
      {...dataGridPaginationProps}
    />
  );
};

/**
 * FeedGrid
 * forwardRefを使ってreload()を外部から呼び出せるようにしている
 */
export const PropertySummaryReuseFeedGrid = forwardRef<
  { reload: () => void },
  PropertySummaryReuseFeedGridProps
>(_FeedGrid);
